import type { TilePosition } from '../../../model/gamestate/GameState';
import { COLORS } from '../constants';
import type { InterpolatedActorData, InterpolatedEntityState } from '../types';
import type { Viewport } from '../viewport';
import type { MapLayerRenderer } from '../MapLayerRenderer';
import { getQueueMarkerSprite } from '../runtimeSpriteCache';

export class QueueRenderer {
    private queuePreview: (TilePosition | null)[] = [];

    public setQueuePreview(queue: (TilePosition | null)[]): void {
        this.queuePreview = queue.slice();
    }

    public drawPathLines(
        ctx: CanvasRenderingContext2D,
        selfState: InterpolatedEntityState<InterpolatedActorData> | null,
        viewport: Viewport,
        mapLayer: MapLayerRenderer
    ): void {
        if (!selfState) {
            return;
        }
        const visualQueue = this.queuePreview;
        if (!visualQueue.length) {
            return;
        }

        ctx.save();
        ctx.beginPath();
        ctx.strokeStyle = COLORS.pathLine;
        ctx.lineWidth = 4;
        ctx.setLineDash([10, 10]);

        const startCenter = viewport.worldToScreenCenter(selfState.current.x, selfState.current.y, mapLayer.getGrid());
        ctx.moveTo(startCenter.x, startCenter.y);

        const grid = mapLayer.getGrid();
        for (const target of visualQueue) {
            if (!target) continue;
            const targetCenter = viewport.worldToScreenCenter(target.x, target.y, grid);
            ctx.lineTo(targetCenter.x, targetCenter.y);
        }

        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
    }

    public drawQueueMarkers(ctx: CanvasRenderingContext2D, viewport: Viewport, mapLayer: MapLayerRenderer): void {
        const visualQueue = this.queuePreview;
        if (!visualQueue.length) {
            return;
        }

        ctx.save();
        const occupancy: Record<string, number> = {};
        const grid = mapLayer.getGrid();

        for (let i = 0; i < visualQueue.length; i += 1) {
            const target = visualQueue[i];
            if (!target) continue;
            const key = `${target.x},${target.y}`;
            const count = occupancy[key] || 0;
            occupancy[key] = count + 1;

            const center = viewport.worldToScreenCenter(target.x, target.y, grid);
            const offsetX = count * 5 - 5;
            const offsetY = count * 5 - 5;
            const marker = getQueueMarkerSprite(String(i + 1));
            ctx.drawImage(
                marker.canvas,
                center.x + offsetX - marker.logicalWidth / 2,
                center.y + offsetY - marker.logicalHeight / 2,
                marker.logicalWidth,
                marker.logicalHeight,
            );
        }
        ctx.restore();
    }
}
